import { useEffect, useMemo, useState } from 'react';

import { Modal, Pressable, useWindowDimensions } from 'react-native';

import { XStack, YStack } from 'tamagui';

import { HeadingS, Icon } from '@south-street-app/atoms';
import { useChatStore } from '@south-street-app/stores';
import { isWeb, refreshUnreadCounts } from '@south-street-app/utils';

import { ArchivedConversationContent } from '../ArchivedChatScreen/ArchivedConversationContent';
import { ArchivedChatsList } from '../ArchivedChatsScreen/ArchivedChatsList';

type ArchivedChatsModalProps = {
  visible: boolean;
  onClose: () => void;
};

const ArchivedChatsModal = ({ visible, onClose }: ArchivedChatsModalProps) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const modalWidth = Math.min(700, screenWidth * 0.9);
  const modalHeight = Math.min(780, screenHeight * 0.9);
  const [selectedUniqueName, setSelectedUniqueName] = useState<string | null>(null);

  const archivedConversations = useChatStore(
    (state) => state.archivedConversations,
  );
  const inboxRefreshKey = useChatStore((state) => state.inboxRefreshKey);
  const selectedConversation = useMemo(
    () =>
      selectedUniqueName
        ? archivedConversations.find(
            (conversation) => conversation.uniqueName === selectedUniqueName,
          ) ?? null
        : null,
    [archivedConversations, selectedUniqueName],
  );

  useEffect(() => {
    if (visible) {
      useChatStore.getState().bumpInboxRefreshKey();

      refreshUnreadCounts().catch(() => undefined);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setSelectedUniqueName(null);
    }
  }, [visible]);

  useEffect(() => {
    if (!isWeb || !visible) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose]);

  return (
    <Modal
      animationType={"fade"}
      onRequestClose={onClose}
      transparent
      visible={visible}
    >
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{ width: modalWidth, height: modalHeight, display: 'flex', flexDirection: 'column' }}
        >
          <YStack
            backgroundColor={'$mono100'}
            borderRadius={'$true'}
            elevation={8}
            flex={1}
            overflow={'hidden'}
            shadowColor={'$mono900'}
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.15}
            shadowRadius={16}
            testID={'customer-archived-chats-modal'}
          >
            <XStack
              alignItems={'center'}
              borderBottomColor={'$mono300'}
              borderBottomWidth={1}
              justifyContent={'space-between'}
              paddingHorizontal={'$4'}
              paddingVertical={'$4'}
            >
              {selectedConversation ? (
                <YStack
                  borderRadius={'$full'}
                  cursor={'pointer' as const}
                  onPress={() => setSelectedUniqueName(null)}
                  padding={'$1'}
                  pressStyle={{ opacity: 0.7 }}
                  testID={'customer-archived-chat-modal-back'}
                >
                  <Icon color={'$mono500'} iconName={'chevronLeftIcon'} size={'$5'} />
                </YStack>
              ) : (
                <HeadingS color={'$mono900'}>{"Archived Chats"}</HeadingS>
              )}
              <YStack
                borderRadius={'$full'}
                cursor={'pointer' as const}
                onPress={onClose}
                padding={'$1'}
                pressStyle={{ opacity: 0.7 }}
              >
                <Icon color={'$mono500'} iconName={'closeIcon'} size={'$5'} />
              </YStack>
            </XStack>

            {selectedConversation ? (
              <YStack flex={1} testID={'customer-archived-chat-detail'}>
                <ArchivedConversationContent conversation={selectedConversation} />
              </YStack>
            ) : (
              <YStack flex={1} testID={'customer-archived-chats-list'}>
                <ArchivedChatsList
                  conversations={archivedConversations}
                  onPressConversation={setSelectedUniqueName}
                  refreshKey={inboxRefreshKey}
                />
              </YStack>
            )}
          </YStack>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export { ArchivedChatsModal };
